import { Children } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import InputCorrection from "./InputCorrection";
import Icon from "../Utils/Icon";

import { useFormatMessage } from "../Intl/Context";
import { useUniqueId } from "../Utils/uniqueId";
import { useCollapseHiddenContentContext } from "../Utils/CollapseContent";

const OptionInput = props => {
  const {
    type,
    option,
    checked,
    onChange,
    isCorrection,
    correctionTextOnlyOnWrongAnswers,
    disabled,
    containerClassName,
    optionProps,
    children
  } = props;

  // If the option is hidden, we don't want to display the name attribute on the input to
  // avoid duplicate names in the DOM, which would make radios fail to be checked when
  // clicking them.
  const { isHidden } = useCollapseHiddenContentContext();
  const name = isHidden ? null : props.name;

  const divClassName = classNames("form-check", containerClassName, {
    "form-check-selected": checked
  });

  const { id: optionId, correctionText } = option;
  // If the component has children, we use them as label, otherwise we use the option name
  // (this allows to pass JSX as label, for instance to add formatting)
  const optionLabel = Children.count(children) > 0 ? children : option.name;

  const t = useFormatMessage();
  const goodUserAnswerLabel = t(intlMessages.goodUserAnswer, { optionLabel });
  const badUserAnswerLabel = t(intlMessages.badUserAnswer, { optionLabel });
  const uncheckedBadOptionLabel = t(intlMessages.uncheckedBadOption, { optionLabel });
  const uncheckedGoodOptionLabel = t(intlMessages.uncheckedGoodOption, { optionLabel });

  const { isCorrect } = option;
  const goodUserAnswer = isCorrection && checked && isCorrect;
  const badUserAnswer = isCorrection && checked && !isCorrect;
  const uncheckedBadOption = isCorrection && !checked && !isCorrect;
  const uncheckedGoodOption = isCorrection && !checked && isCorrect;
  const answerWithoutError = isCorrection && (goodUserAnswer || uncheckedBadOption);

  const displayCorrection =
    isCorrection && (!answerWithoutError || !correctionTextOnlyOnWrongAnswers);

  const correctionIconClassName =
    (isCorrect && "fa-check text-success") || (badUserAnswer && "fa-times text-danger");

  const srOnlyCorrectionText =
    (goodUserAnswer && goodUserAnswerLabel) ||
    (badUserAnswer && badUserAnswerLabel) ||
    (uncheckedBadOption && uncheckedBadOptionLabel) ||
    (uncheckedGoodOption && uncheckedGoodOptionLabel);

  const LabelTagName = isCorrection ? "div" : "label";
  const inputId = useUniqueId("option-input-");
  const labelFor = isCorrection ? null : inputId;

  const labelClassName = classNames("form-check-label", { "flex-1": optionLabel });

  return (
    <div className={divClassName}>
      <div className="d-flex align-items-center">
        {isCorrection && (
          <div className="form-check-correction-icon-container">
            {correctionIconClassName && (
              <Icon className={`fa fa-fw ${correctionIconClassName} margin-right-md`} />
            )}

            {srOnlyCorrectionText && <p className="sr-only">{srOnlyCorrectionText}</p>}
          </div>
        )}

        <LabelTagName htmlFor={labelFor} className={labelClassName}>
          {!isCorrection && (
            <input
              id={inputId}
              type={type}
              name={name}
              value={optionId}
              checked={checked}
              className="form-check-input"
              onChange={onChange}
              disabled={disabled || isCorrection}
              {...optionProps}
            />
          )}

          {optionLabel && <span className="form-check-label-inner">{optionLabel}</span>}
        </LabelTagName>
      </div>

      {displayCorrection && <InputCorrection correctionText={correctionText} />}
    </div>
  );
};

export default OptionInput;

const intlMessages = defineMessages({
  goodUserAnswer: {
    id: "input.option.goodUserAnswer",
    defaultMessage: "Bonne réponse choisie : {optionLabel}"
  },
  badUserAnswer: {
    id: "input.option.badUserAnswer",
    defaultMessage: "Mauvaise réponse choisie : {optionLabel}"
  },
  uncheckedBadOption: {
    id: "input.option.uncheckedBadOption",
    defaultMessage: "Mauvaise réponse non choisie : {optionLabel}"
  },
  uncheckedGoodOption: {
    id: "input.option.uncheckedGoodOption",
    defaultMessage: "Bonne réponse non choisie : {optionLabel}"
  }
});
